import { Space, Typography } from "antd";
import {
  COLORS,
  getAmenityStatus,
  sparseTimeFrameFormat,
  timeFrameTimeFormat,
  timeIsInRange,
} from "~/utils";
import {
  PrecomputedAmenityData,
  RecurringScheduleItems,
  ScheduleItemList,
  Unavailability,
} from "~/types";
import { useState, useEffect } from "react";
import { AvailabilityStatusTag, CustomTextField } from "~/components";
import dayjs from "dayjs";
import { AmenitySchedule } from "../AmenitySchedule";
import { useAppTranslation } from "~/hooks/useAppTranslation";

var minMax = require("dayjs/plugin/minMax");
dayjs.extend(minMax);
var weekday = require("dayjs/plugin/weekday");
dayjs.extend(weekday);

export const AmenityAvailabilityTab = ({
  amenityData,
  scheduleItems,
  recurringScheduleItems,
}: {
  amenityData: PrecomputedAmenityData;
  scheduleItems: ScheduleItemList[];
  recurringScheduleItems: RecurringScheduleItems[];
}) => {
  const { t } = useAppTranslation();
  const days = [
    t("Sunday"),
    t("Monday"),
    t("Tuesday"),
    t("Wednesday"),
    t("Thursday"),
    t("Friday"),
    t("Saturday"),
  ];

  const availability = amenityData.availability;

  const state = getAmenityStatus(amenityData);
  const [status, setStatus] = useState(state);

  // Keep the status up-to-date; updates the status every minute on the minute
  useEffect(() => {
    const now = dayjs();
    const toExactMinute = 1000 - now.millisecond() + (60 - now.second()) * 1000;
    let statusTimer: ReturnType<typeof setInterval> | undefined;
    let statusTimeout: ReturnType<typeof setTimeout> | undefined;
    statusTimeout = setTimeout(() => {
      statusTimer = setInterval(() => {
        setStatus(getAmenityStatus(amenityData));
      }, 1000 * 60); // every minute
      setStatus(getAmenityStatus(amenityData));
    }, toExactMinute);
    return () => {
      if (statusTimeout !== undefined) clearTimeout(statusTimeout);
      if (statusTimer !== undefined) clearInterval(statusTimer);
    };
  });

  if (availability.fullyUnavailable === true)
    return (
      <>
        <br />
        <Typography.Text>{t("No_availability_provided")}</Typography.Text>
      </>
    );

  const noAvail = t("No_Availability");
  let display: string[] = [];
  for (let day = 0; day < 7; day++) {
    if (availability.available[day].length === 0) display[day] = noAvail;
    else
      display[day] = availability.available[day]
        .map((value) => {
          return timeFrameTimeFormat(value);
        })
        .join("; ");
  }

  const getExceptions = (
    columnHeader: string,
    filter: (value: Unavailability) => boolean
  ) =>
    [
      <>
        <Typography.Text strong>{columnHeader}</Typography.Text>
        <Typography.Text strong>{t("Reason")}</Typography.Text>
      </>,
    ].concat(
      availability.unavailable.filter(filter).map((value) => {
        const inRange = timeIsInRange(value.timeFrame, dayjs());
        return (
          <>
            <Typography.Text
              style={{
                marginRight: 10,
                color: inRange ? COLORS.dark_red : undefined,
              }}
            >
              {sparseTimeFrameFormat(value.timeFrame)}
            </Typography.Text>
            <CustomTextField value={value.reason} onEmpty="none" />
          </>
        );
      })
    );

  const now = dayjs();

  const activeExceptions = getExceptions(
    "Active Exceptions",
    (value) =>
      value.timeFrame.end.isAfter(now) && value.timeFrame.start.isBefore(now)
  );
  const futureExceptions = getExceptions("Future Exceptions", (value) =>
    value.timeFrame.start.isAfter(now)
  );

  const exceptions = (
    activeExceptions.length === 1 ? [] : activeExceptions
  ).concat(futureExceptions.length === 1 ? [] : futureExceptions);

  return (
    <Space
      direction="vertical"
      style={{
        width: "100%",
      }}
    >
      <Typography.Title level={5}>{t("Current_Status")}</Typography.Title>
      <div>
        <AvailabilityStatusTag status={status[0]} />
        {status[1] ? (
          <div style={{ margin: "5px 0" }}>
            <Typography.Text strong>{t("Reason")}</Typography.Text>
            <CustomTextField value={status[1]} onEmpty="none" />
          </div>
        ) : (
          <></>
        )}
      </div>
      <Typography.Title level={5}>{t("Availability")}</Typography.Title>
      {exceptions.length !== 0 && (
        <div className="amenity-exception-grid">{exceptions}</div>
      )}
      <Typography.Text strong>{t("Availability")}</Typography.Text>
      <div className="amenity-availability-grid">
        {display.map((value, index) => {
          const today = index === dayjs().day();
          return (
            <>
              <Typography.Text
                style={{
                  marginRight: 10,
                  fontWeight: today ? "bold" : undefined,
                  color: today ? COLORS.dark_green : undefined,
                }}
              >
                {days[index]}:
              </Typography.Text>
              <Typography.Text>{value}</Typography.Text>
            </>
          );
        })}
      </div>

      <Typography.Text strong>{t("Schedule")}</Typography.Text>
      <AmenitySchedule
        scheduleItems={scheduleItems}
        recurringScheduleItems={recurringScheduleItems}
      />
    </Space>
  );
};
